import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/Toast';
import { colors } from '../theme/tokens';

const FulfillRequests = () => {
  const { token, canAddData } = useAuth();
  const navigate = useNavigate();
  const showToast = useToast();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [parts, setParts] = useState([{ position: '', code: '', quantity: '', unit: 'x' }]);
  const [submitting, setSubmitting] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    if (!canAddData) {
      navigate('/');
      return;
    }
    fetchRequests();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAddData, navigate]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/requests/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
    } catch (err) {
      console.error('Error fetching requests:', err);
      showToast('Failed to load requests', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePartChange = (index, e) => {
    const updated = [...parts];
    updated[index][e.target.name] = e.target.value;
    setParts(updated);
  };

  const addPartField = () => {
    setParts([...parts, { position: '', code: '', quantity: '', unit: 'x' }]);
  };

  const removePartField = (index) => {
    setParts(parts.filter((_, i) => i !== index));
  };

  const handleFulfillRequest = async (requestId) => {
    const validParts = parts.filter(p => p.position && p.code && p.quantity);
    
    if (validParts.length === 0) {
      showToast('Please add at least one part with all details filled in', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/requests/${requestId}/fulfill`,
        {
          parts: validParts.map(p => ({
            position: p.position,
            code: p.code,
            quantity: Number(p.quantity),
            unit: p.unit
          }))
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      showToast('Request fulfilled! Your submission is pending admin approval.', 'success');
      setSelectedRequest(null);
      setParts([{ position: '', code: '', quantity: '', unit: 'x' }]);
      fetchRequests();
    } catch (err) {
      showToast('Failed to fulfill request: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading requests...</div>;
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Fulfill Part Requests</h1>
      <p style={styles.subtitle}>
        Help the community by providing part data for requested cars
      </p>

      {requests.length === 0 && (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📭</div>
          <p>No pending requests at the moment!</p>
          <p style={styles.emptyHint}>Check back later or add your own data.</p>
        </div>
      )}

      {selectedRequest ? (
        // Fulfillment Form
        <div style={styles.fulfillmentCard}>
          <button 
            style={styles.backBtn} 
            onClick={() => {
              setSelectedRequest(null);
              setParts([{ position: '', code: '', quantity: '', unit: 'x' }]);
            }}
          >
            ← Back to Requests
          </button>

          <div style={styles.requestInfo}>
            <h2 style={styles.requestTitle}>Fulfill Request</h2>
            <div style={styles.carInfo}>
              <div style={styles.infoRow}>
                <strong>Make:</strong> {selectedRequest.make}
              </div>
              <div style={styles.infoRow}>
                <strong>Model:</strong> {selectedRequest.model}
              </div>
              <div style={styles.infoRow}>
                <strong>Variant:</strong> {selectedRequest.variant}
              </div>
              <div style={styles.infoRow}>
                <strong>Year Range:</strong> {selectedRequest.yearRange.from} - {selectedRequest.yearRange.to}
              </div>
              <div style={styles.infoRow}>
                <strong>Requested Part:</strong> <span style={styles.highlight}>{selectedRequest.requestedPart}</span>
              </div>
              <div style={styles.infoRow}>
                <strong>Requested by:</strong> {selectedRequest.requestedBy?.name} ({selectedRequest.requestedBy?.tier.toUpperCase()})
              </div>
            </div>
            
            {selectedRequest.photoPath && (
              <div style={styles.requestPhotoLarge}>
                <h4 style={{marginTop: '15px', marginBottom: '10px', color: colors.text}}>Reference Photo:</h4>
                <img 
                  src={`${API_URL}/api/request-images/${selectedRequest.photoPath}`}
                  alt="Requested part reference"
                  style={styles.largePhoto}
                />
              </div>
            )}
          </div>

          <div style={styles.partsSection}>
            <h3>Add Part Data</h3>
            {parts.map((part, index) => (
              <div key={index} style={styles.partRow}>
                <input
                  name="position"
                  placeholder="Position (e.g., Front Door Seal)"
                  value={part.position}
                  onChange={(e) => handlePartChange(index, e)}
                  style={styles.input}
                />
                <input
                  name="code"
                  placeholder="Part Code (SKU)"
                  value={part.code}
                  onChange={(e) => handlePartChange(index, e)}
                  style={styles.input}
                />
                <input
                  name="quantity"
                  placeholder="Qty"
                  type="number"
                  value={part.quantity}
                  onChange={(e) => handlePartChange(index, e)}
                  style={{...styles.input, width: '80px'}}
                />
                <select
                  name="unit"
                  value={part.unit}
                  onChange={(e) => handlePartChange(index, e)}
                  style={{...styles.input, width: '80px'}}
                >
                  <option value="x">pieces (x)</option>
                  <option value="m">meters (m)</option>
                </select>
                {parts.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removePartField(index)}
                    style={styles.removeBtn}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            <button type="button" onClick={addPartField} style={styles.addPartBtn}>
              + Add Another Part
            </button>
          </div>

          <button
            onClick={() => handleFulfillRequest(selectedRequest._id)}
            style={styles.submitBtn}
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit for Approval'}
          </button>
        </div>
      ) : (
        // Requests List
        <div style={styles.requestsList}>
          {requests.map((request) => (
            <div key={request._id} style={styles.requestCard}>
              <div style={styles.requestHeader}>
                <h3 style={styles.carTitle}>
                  {request.make} {request.model} {request.variant}
                </h3>
                <span style={styles.yearBadge}>
                  {request.yearRange.from} - {request.yearRange.to}
                </span>
              </div>
              
              <div style={styles.requestBody}>
                <div style={styles.requestedPartBox}>
                  <span style={styles.partLabel}>Requested Part:</span>
                  <span style={styles.partName}>{request.requestedPart}</span>
                </div>

                {request.photoPath && (
                  <div style={styles.photoContainer}>
                    <img 
                      src={`${API_URL}/api/request-images/${request.photoPath}`}
                      alt="Requested part"
                      style={styles.requestPhoto}
                    />
                  </div>
                )}
                
                <div style={styles.requesterInfo}>
                  <span>👤 {request.requestedBy?.name}</span>
                  <span style={styles.tierBadge}>
                    {request.requestedBy?.tier.toUpperCase()}
                  </span>
                  <span style={styles.date}>
                    {new Date(request.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <button
                onClick={() => setSelectedRequest(request)}
                style={styles.fulfillBtn}
              >
                Fulfill This Request
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { maxWidth: '1000px', margin: '20px auto', padding: '20px' },
  loading: { textAlign: 'center', padding: '50px', fontSize: '18px', color: colors.text },
  title: { textAlign: 'center', color: colors.text, marginBottom: '10px' },
  subtitle: { textAlign: 'center', color: colors.textMuted, marginBottom: '30px' },
  emptyState: { textAlign: 'center', padding: '60px 20px', background: colors.surface, color: colors.text, borderRadius: '15px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', border: `1px solid ${colors.border}` },
  emptyIcon: { fontSize: '64px', marginBottom: '20px' },
  emptyHint: { color: colors.textFaint, fontSize: '14px' },
  requestsList: { display: 'flex', flexDirection: 'column', gap: '15px' },
  requestCard: { background: colors.surface, color: colors.text, padding: '20px', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', border: `1px solid ${colors.border}` },
  requestHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' },
  carTitle: { color: '#2fb65c', margin: 0 },
  yearBadge: { background: colors.greenSoft, padding: '5px 15px', borderRadius: '15px', fontSize: '14px', color: colors.text, fontWeight: '600' },
  requestBody: { marginBottom: '15px' },
  requestedPartBox: { background: colors.amberSoft, padding: '12px', borderRadius: '8px', marginBottom: '10px', border: '1px solid #91C83E' },
  partLabel: { fontWeight: '600', marginRight: '10px', color: colors.text },
  partName: { color: '#2fb65c', fontSize: '16px', fontWeight: 'bold' },
  requesterInfo: { display: 'flex', gap: '15px', alignItems: 'center', fontSize: '14px', color: colors.textMuted },
  tierBadge: { background: '#91C83E', color: 'white', padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold' },
  date: { color: colors.textFaint, fontSize: '12px' },
  photoContainer: { marginTop: '10px', marginBottom: '10px' },
  requestPhoto: { maxWidth: '200px', maxHeight: '150px', borderRadius: '8px', objectFit: 'cover', border: '2px solid #2fb65c' },
  fulfillBtn: { width: '100%', padding: '12px', background: '#2fb65c', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' },
  fulfillmentCard: { background: colors.surface, color: colors.text, padding: '30px', borderRadius: '15px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', border: `1px solid ${colors.border}` },
  backBtn: { background: colors.surface2, color: colors.text, border: 'none', padding: '10px 20px', borderRadius: '5px', cursor: 'pointer', marginBottom: '20px', fontSize: '14px' },
  requestInfo: { background: colors.greenSoft, padding: '20px', borderRadius: '10px', marginBottom: '25px', border: '2px solid #91C83E' },
  requestTitle: { color: '#2fb65c', marginTop: 0 },
  carInfo: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  infoRow: { fontSize: '14px', color: colors.text },
  highlight: { color: '#2fb65c', fontWeight: 'bold', fontSize: '16px' },
  requestPhotoLarge: { marginTop: '15px', padding: '15px', background: colors.surfaceSoft, borderRadius: '8px' },
  largePhoto: { width: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: '8px', border: '2px solid #2fb65c' },
  partsSection: { marginBottom: '20px' },
  partRow: { display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' },
  input: { padding: '10px', border: `1px solid ${colors.border}`, borderRadius: '5px', fontSize: '14px', flex: 1, background: colors.inputBg, color: colors.text },
  removeBtn: { padding: '10px 12px', background: colors.red, color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' },
  addPartBtn: { padding: '8px 16px', background: '#91C83E', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '14px' },
  submitBtn: { width: '100%', padding: '15px', background: '#2fb65c', color: 'white', border: 'none', borderRadius: '8px', fontSize: '18px', fontWeight: '600', cursor: 'pointer' }
};

export default FulfillRequests;
